import React, { Fragment, useState, useEffect } from 'react';
import validator from 'validator';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { parsePhoneNumber } from 'libphonenumber-js';

import { Input, Button, IconGraphy, Typography, colors, Tooltip } from '@leapeasy/ui-kit';

import { setDataAction } from 'store/actions/claimFormActions';

// Images
import Img_Alert from 'assets/alert.png';

const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;

  try {
    const result = parsePhoneNumber(phoneNumber, 'US');
    return result.isValid();
  } catch (e) {
    return false;
  }
};

const Form4 = (props) => {
  const { actions, token, formData, handleNext, handleBack } = props;

  const [data, setFormData] = useState({
    name: null,
    phone: null,
    email: null,
    title: null,
    secondaryName: null,
    secondaryPhone: null,
    secondaryTitle: null,
    secondaryEmail: null,
    secondaryAdded: false,
    check: false,
  });

  const [valid, setValid] = useState({
    name: true,
    phone: true,
    email: true,
    title: true,
    secondaryName: true,
    secondaryPhone: true,
    secondaryTitle: true,
    secondaryEmail: true,
    secondaryAdded: false,
  });

  const [claimerFlag, setClaimerFlag] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: formData.data.contactName ?? prev.name,
      phone: formData.data.contactPhone ?? prev.phone,
      email: formData.data.contactEmail ?? prev.email,
      title: formData.data.contactTitle ?? prev.title,
      secondaryName: formData.data.secondaryName ?? prev.secondaryName,
      secondaryPhone: formData.data.secondaryPhone ?? prev.secondaryPhone,
      secondaryTitle: formData.data.secondaryTitle ?? prev.secondaryTitle,
      secondaryEmail: formData.data.secondaryEmail ?? prev.secondaryEmail,
      secondaryAdded:
        formData.data.contactName != null
          ? Boolean(formData.data.secondaryAdded)
          : prev.secondaryAdded,
      check: true,
    }));
  }, [
    formData.data.contactName,
    formData.data.contactPhone,
    formData.data.contactEmail,
    formData.data.contactTitle,
    formData.data.secondaryName,
    formData.data.secondaryPhone,
    formData.data.secondaryTitle,
    formData.data.secondaryEmail,
    formData.data.secondaryAdded,
  ]);

  const handleChange = (e, label) => {
    const value = e.target.value;
    if (label === 'check') {
      setFormData({ ...data, [label]: e.target.checked });
    } else {
      setFormData({ ...data, [label]: value });
    }
    if (label === 'email') {
      setValid({ ...valid, email: validator.isEmail(value) });
    }

    if (label === 'phone') {
      setValid({
        ...valid,
        phone: validatePhoneNumber(value) ? true : false,
      });
    }

    if (label === 'secondaryEmail') {
      setValid({ ...valid, secondaryEmail: validator.isEmail(value) });
    }
    if (label === 'secondaryPhone') {
      setValid({
        ...valid,
        secondaryPhone: validatePhoneNumber(value) ? true : false,
      });
    }
    if (label === 'secondaryName') {
      setValid({ ...valid, secondaryName: true });
    }

    if (label === 'secondaryTitle') {
      setValid({ ...valid, secondaryTitle: true });
    }
  };

  const addSecondaryContactInfo = () => {
    setFormData((prev) => ({ ...prev, secondaryAdded: true }));
  };

  const removeSecondaryContactInfo = () => {
    setFormData((prev) => ({
      ...prev,
      secondaryAdded: false,
      secondaryName: null,
      secondaryPhone: null,
      secondaryTitle: null,
      secondaryEmail: null,
    }));
    setValid((prev) => ({
      ...prev,
      secondaryName: true,
      secondaryPhone: true,
      secondaryTitle: true,
      secondaryEmail: true,
    }));
  };

  const isPrimaryComplete =
    Boolean(data.name?.trim()) &&
    Boolean(data.title?.trim()) &&
    Boolean(data.email?.trim()) &&
    Boolean(data.phone) &&
    validator.isEmail(data.email || '') &&
    validatePhoneNumber(data.phone || '');

  const isSecondaryComplete =
    !data.secondaryAdded ||
    (Boolean(data.secondaryName?.trim()) &&
      Boolean(data.secondaryTitle?.trim()) &&
      Boolean(data.secondaryEmail?.trim()) &&
      Boolean(data.secondaryPhone) &&
      validator.isEmail(data.secondaryEmail || '') &&
      validatePhoneNumber(data.secondaryPhone || ''));

  const handleNextButton = () => {
    const secondaryPayload = data.secondaryAdded
      ? {
          secondaryAdded: true,
          secondaryName: data.secondaryName,
          secondaryPhone: data.secondaryPhone,
          secondaryTitle: data.secondaryTitle,
          secondaryEmail: data.secondaryEmail,
        }
      : {
          secondaryAdded: false,
          secondaryName: null,
          secondaryPhone: null,
          secondaryTitle: null,
          secondaryEmail: null,
        };

    actions.setDataAction({
      ...formData.data,
      contactName: data.name,
      contactEmail: data.email,
      contactPhone: data.phone,
      contactTitle: data.title,
      ...secondaryPayload,
    });
    handleNext();
  };

  const showClaimer = () => {
    handleNextButton();
  };

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="4px">
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            padding: '24px',
            paddingBottom: '0',
            marginBottom: '24px',
            gap: '12px',
            background: 'rgba(249, 248, 249, 0.8)',
            border: '1px solid #F8F4F9',
            borderRadius: '8px',
          }}
        >
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              Primary Claim Contact (Required)
              <Tooltip
                placement="bottom"
                showCarot
                title={`The primary property contact for the claim.  Please be sure that the information entered on the claim form for this person is accurate.  Any inaccurate information will result in delayed claim processing.`}
                variant="contained"
              >
                <IconGraphy
                  width="14px"
                  height="14px"
                  icon={'General.QuestionMark'}
                  style={{
                    cursor: 'pointer',
                    marginBottom: '4px',
                    border: '1px solid #2E0F40',
                    borderRadius: '50px',
                  }}
                />
              </Tooltip>
            </Box>
          </Typography>

          <Input
            label="Contact Name"
            value={data.name}
            onChange={(e) => handleChange(e, 'name')}
            type="text"
            minWidth="100%"
            fullWidth
            size="medium"
            showCopy={false}
            icon={<IconGraphy icon="Users.User" style={{ width: 16, height: 16 }} />}
          />

          <Input
            label="Contact Title"
            value={data.title}
            onChange={(e) => handleChange(e, 'title')}
            type="text"
            minWidth="100%"
            fullWidth
            size="medium"
            showCopy={false}
            icon={<IconGraphy icon="EducationWork.WorkOutline" />}
          />

          <Input
            label="Contact Email"
            value={data.email}
            onChange={(e) => handleChange(e, 'email')}
            type="text"
            minWidth="100%"
            fullWidth
            size="medium"
            errorType={!valid.email && data.email ? 'error' : 'none'}
            helperMessage={!valid.email && data.email ? 'Invalid Input' : ''}
            showCopy={false}
            showHelperOnTyping={false}
            icon={<IconGraphy icon="Notifications.Email" />}
          />

          <Input
            label="Contact Phone Number"
            value={data.phone}
            onChange={(e) => handleChange(e, 'phone')}
            type="tel"
            minWidth="100%"
            fullWidth
            size="medium"
            phoneCountry="US"
            errorType={!valid.phone && data.phone ? 'error' : 'none'}
            helperMessage={!valid.phone && data.phone && 'Invalid Input'}
            showCopy={false}
            showHelperOnTyping={false}
          />
        </Box>
      </Box>

      <Button
        size="medium"
        variant="secondary"
        onClick={addSecondaryContactInfo}
        disabled={data.secondaryAdded}
        style={{ flexShrink: 1, alignSelf: 'flex-start' }}
      >
        Add contact
      </Button>

      {data.secondaryAdded && (
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            marginTop: '24px',
            padding: '24px',
            paddingBottom: '0',
            marginBottom: '24px',
            gap: '12px',
            background: 'rgba(249, 248, 249, 0.8)',
            border: '1px solid #F8F4F9',
            borderRadius: '8px',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <Typography variant="body1" style={{ color: colors.purple[900] }}>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                Secondary Claim Contact (Optional)
                <Tooltip
                  placement="bottom"
                  showCarot
                  title={`The secondary property contact for this claim. Please be sure that the information entered on the claim form for this person is accurate. Any inaccurate information will result in delayed claim processing.`}
                  variant="contained"
                >
                  <IconGraphy
                    width="14px"
                    height="14px"
                    icon={'General.QuestionMark'}
                    style={{
                      cursor: 'pointer',
                      marginBottom: '3px',
                      border: '1px solid #2E0F40',
                      borderRadius: '50px',
                    }}
                  />
                </Tooltip>
              </Box>
            </Typography>
            <Tooltip title="Remove secondary contact" placement="left">
              <IconButton
                size="small"
                onClick={removeSecondaryContactInfo}
                aria-label="Remove secondary contact"
                sx={{ color: colors.purple[900], padding: '4px' }}
              >
                <IconGraphy icon="General.Delete" width="18px" height="18px" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box display="flex" flexDirection="column" gap="4px">
            <Input
              label="Contact Name"
              value={data.secondaryName}
              onChange={(e) => handleChange(e, 'secondaryName')}
              type="text"
              minWidth="100%"
              fullWidth
              size="medium"
              showCopy={false}
              icon={<IconGraphy icon="Users.User" style={{ width: 16, height: 16 }} />}
            />

            <Input
              label="Contact Title"
              value={data.secondaryTitle}
              onChange={(e) => handleChange(e, 'secondaryTitle')}
              type="text"
              minWidth="100%"
              fullWidth
              size="medium"
              showCopy={false}
              icon={<IconGraphy icon="EducationWork.WorkOutline" />}
            />

            <Input
              label="Contact Email"
              value={data.secondaryEmail}
              onChange={(e) => handleChange(e, 'secondaryEmail')}
              type="text"
              minWidth="100%"
              fullWidth
              size="medium"
              errorType={!valid.secondaryEmail && data.secondaryEmail ? 'error' : 'none'}
              helperMessage={!valid.secondaryEmail && data.secondaryEmail ? 'Invalid Input' : ''}
              showCopy={false}
              showHelperOnTyping={false}
              icon={<IconGraphy icon="Notifications.Email" />}
            />

            <Input
              label="Contact Phone Number"
              value={data.secondaryPhone}
              onChange={(e) => handleChange(e, 'secondaryPhone')}
              type="tel"
              minWidth="100%"
              fullWidth
              size="medium"
              phoneCountry="US"
              errorType={!valid.secondaryPhone && data.secondaryPhone ? 'error' : 'none'}
              helperMessage={!valid.secondaryPhone && data.secondaryPhone && 'Invalid Input'}
              showCopy={false}
              showHelperOnTyping={false}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', mt: 4, gap: '16px' }}
      >
        <Button size="medium" variant="secondary" onClick={handleBack} style={{ flexShrink: 2 }}>
          Back
        </Button>

        <Button
          size="medium"
          variant="primary"
          onClick={handleNextButton}
          disabled={!(isPrimaryComplete && isSecondaryComplete)}
          style={{ flexShrink: 1 }}
        >
          Next
        </Button>
      </Box>
      <Box marginTop="24px">
        <Box
          backgroundColor="#E8F1FE"
          borderRadius="8px"
          padding="12px"
          display="flex"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap="10px"
        >
          <Box>
            <img src={Img_Alert} alt="alert" />
          </Box>

          <Box flex={1} color="#175EC2" fontSize="13px" fontWeight="400">
            <Box fontWeight="600" color="#175EC2">
              Important Notice!
            </Box>
            Please ensure the any contact information entered on this page is accurate and is the
            information for the person(s) responsible for providing any and all needed claim
            information that is necessary for processing this claim. Any inaccurate information will
            result in delayed claim processing.
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    formData: state.getIn(['formData']),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        setDataAction,
      },
      dispatch,
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form4);
